import { useRef, useEffect, useCallback } from "react";

export const useWakeLock = (enabled: boolean) => {
  const wakeLock = useRef<WakeLockSentinel | null>(null);

  const requestWakeLock = useCallback(async () => {
    if (!("wakeLock" in navigator)) {
      console.warn("Wake Lock API is not supported in this browser.");
      return;
    }

    try {
      wakeLock.current = await navigator.wakeLock.request("screen");
      console.log("Wake Lock is active");

      wakeLock.current.addEventListener("release", () => {
        console.log("Wake Lock was released");
      });
    } catch (err) {
      console.error(`Wake Lock request failed: ${err}`);
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLock.current) {
      await wakeLock.current.release();
      wakeLock.current = null;
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      releaseWakeLock();
    };
  }, [enabled, requestWakeLock, releaseWakeLock]);

  // Re-request wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        enabled &&
        wakeLock.current === null &&
        document.visibilityState === "visible"
      ) {
        await requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, requestWakeLock]);
};
